import { ActionIcon, Box, LoadingOverlay, Modal } from '@mantine/core';
import dayjs from 'dayjs';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../components/elements/Button';
import ButtonLink from '../../components/elements/ButtonLink';
import DataTable from '../../components/elements/DataTable';
import ScrollToTop from '../../components/elements/ScrollToTop';
import Text from '../../components/fields/Text';
import NotificationEmpty from '../../components/fragments/NotificationEmpty';
import IconAdd from '../../components/icons/IconAdd';
import IconTrash from '../../components/icons/IconTrash';
import { AppContext } from '../../contexts';
import { thousand } from '../../utils/format';
import { useDebounce } from '../../utils/hooks';
import { fetchActiveNotification, fetchDeleteNotification, fetchNotification, postOrderingNotificationActive } from './action';
import styles from './styles.scoped.css';
import SortableCard from './SortableCard';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, arrayMove, sortableKeyboardCoordinates, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import Divider from '../../components/elements/Divider';
import { useNavigate } from 'react-router-dom';
import IconPencil from '../../components/icons/IconPencil';

const Notification = () => {
  const {
    dataActiveNotification: { data: activeData, loading },
    orderActiveNotification,
    deleteNotification,
    dataNotification }
    = useSelector((s) => s.notification);
  const [dataActive, setDataActive] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState({ isShow: false, data: {}, loading: false });
  const { setBreadcrumbs } = useContext(AppContext);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 500);
  const inputRef = useRef(null);
  const dispatch = useDispatch();
  const container = document.getElementById('children-container');
  const navigate = useNavigate();

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    setBreadcrumbs(['Notifikasi Login']);
    dispatch(fetchActiveNotification());
    dispatch(fetchNotification(1, search));
  }, []);

  useEffect(() => {
    container?.scroll({ top: 0, behavior: 'smooth' });
  }, [loading]);

  useEffect(() => {
    if (activeData?.length > 0) {
      setDataActive(activeData?.map((item, index) => ({
        ...item,
        orderNumber: index + 1
      })));
    } else {
      setDataActive([]);
    }
  }, [activeData]);

  useEffect(() => {
    if (!orderActiveNotification.loading) {
      dispatch(fetchActiveNotification());
    }
  }, [orderActiveNotification]);

  useEffect(() => {
    dispatch(fetchNotification(1, search));
  }, [debouncedSearch]);

  useEffect(() => {
    if (!deleteNotification.loading && deleteNotification.success) {
      setDeleteConfirm({ isShow: false, data: {}, loading: false });
      dispatch(fetchActiveNotification());
      dispatch(fetchNotification(1, ''));
    }
  }, [deleteNotification]);

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const activeIndex = dataActive.findIndex(item => item.id === active.id);
    const overIndex = dataActive.findIndex(item => item.id === over.id);

    if (activeIndex !== -1 && overIndex !== -1) {
      const newOrder = arrayMove(dataActive, activeIndex, overIndex);
      setDataActive(newOrder);

      const ids = newOrder?.map(item => item.id);
      dispatch(postOrderingNotificationActive(ids));
    }
  };

  const handleDeleteNotif = () => {
    setDeleteConfirm({ ...deleteConfirm, loading: true });
    dispatch(fetchDeleteNotification(deleteConfirm.data.id));
  };

  const column = [
    { heading: 'Judul', value: 'title' },
    { heading: 'Isi', value: (value) => renderContent(value) },
    { heading: 'Gambar', value: (value) => value.thumbnail ? <img alt="thumbnail" src={value.thumbnail} /> : <span>-</span> },
    { heading: 'Status', value: ({ isActive }) => isActive !== undefined ? renderStatus(isActive) : renderStatus(true) },
    {
      heading: 'Periode', className: 'rowTable', value: ({ startDate, endDate }) =>
        `${dayjs(startDate).format('DD MMM YYYY')} - ${dayjs(endDate).format('DD MMM YYYY')}`
    },
    { heading: 'Action', value: (value) => renderActionUpdate(value) }
  ];

  const renderContent = (value) => {
    const regex = /(<([^>]+)>)/gi;
    const newString = value.type === 'text' ? value.content.replace(regex, ' ').substring(0, 50) : '-';
    return (
      value.type === 'text-image' ? (
        <span>{`${value.description.substring(0, 50)}...`}</span>
      ) : (
        <span>{`${newString}`}</span>
      )
    );
  };

  const renderActionUpdate = (data) => {
    return (
      <div className={styles.buttonWrapper}>
        <ActionIcon onClick={() => navigate(`/notification/edit/${data.id}`)}>
          <IconPencil style={{ fill: 'white' }} />
        </ActionIcon>
        <ActionIcon bg={'#CF212A'} onClick={() => setDeleteConfirm({ isShow: true, data })}>
          <IconTrash style={{ fill: 'white' }} />
        </ActionIcon>
      </div>
    );
  };

  const renderStatus = (status) => {
    return (
      <label className={status ? styles.labelSuccess : styles.labelDanger}>
        {status ? 'Aktif' : 'Tidak Aktif'}
      </label>
    );
  };

  return (
    <div className={styles.main}>
      <section className={styles['notification-wrapper']}>
        <h4>Notifikasi Aktif di MAP Lite</h4>
        {dataActive?.length === 0 ? (
          <div className={styles.emptyNotification}>
            <NotificationEmpty isButtonAdd={false} />
          </div>
        ) : (
          <>
            <small>
              Maksimal menampilkan 3 notifikasi aktif.
              Urutan notifikasi akan ditampilkan sesuai dengan urutan dibawah ini
            </small>
            <div className={styles.sortableCardsContainer}>
              <DndContext
                collisionDetection={closestCenter}
                modifiers={[restrictToVerticalAxis]}
                onDragEnd={handleDragEnd}
                sensors={sensors}
              >
                <SortableContext
                  items={dataActive?.map(item => item.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {dataActive?.map((item) => (
                    <SortableCard
                      id={item.id}
                      item={item}
                      key={item.id}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
          </>
        )}
      </section>

      <section className={styles['notification-wrapper']}>
        <div className={styles['action-wrapper']}>
          <div className={styles.action}>
            <ButtonLink
              className={styles.button}
              to={'/notification/add'}
              type="primary">
              <IconAdd style={{ fill: '#FFFFFF' }} /> Tambah Notifikasi
            </ButtonLink>
            <span>Menampilkan&nbsp;
              <strong>{thousand(dataNotification?.meta?.totalData)}</strong> data</span>
          </div>
          <div className={styles.filters}>
            <Text
              className={styles['search-bar']}
              hideLabel
              inputProps={{
                ref: inputRef,
                placeholder: 'Cari data...',
                icon: 'ic-search.svg',
                value: search,
                onChange: (e) => setSearch(e.target.value),
              }}
            />
          </div>
        </div>
      </section>

      <section className={styles['notification-wrapper']}>
        {dataNotification?.data.length === 0 ? (<NotificationEmpty />) : (
          <div className={styles['table-wrapper']}>
            <DataTable
              column={column}
              data={dataNotification?.data}
              onEndReached={() => {
                if (dataNotification?.meta?.totalData > 10) {
                  dispatch(fetchNotification(dataNotification?.meta.page + 1, search, false));
                }
              }}
              styles={'table'}
              withIndex
            />
          </div>
        )}
      </section>
      <Modal
        centered
        onClose={() => setDeleteConfirm({ isShow: false, data: {} })}
        opened={deleteConfirm.isShow}
        withCloseButton={false}>
        <Box className={styles['modal-wrapper']}>
          <LoadingOverlay overlayProps={{ radius: 'sm', blur: 2 }} visible={deleteConfirm.loading} zIndex={1000} />
          <div className={styles['label']}>Hapus Notifikasi</div>
          <Divider />
          <p style={{ textAlign: 'center' }}>Apakah kamu yakin akan menghapus notifikasi <strong>{deleteConfirm.data.title}</strong>?</p>
          <div className={styles['buttonWrapper']}>
            <Button className={styles['btn-close-modal']} onClick={() => setDeleteConfirm({ isShow: false, data: {} })} outlined style={'blue'} type="button">Batal</Button>
            <Button className={styles['btn-close-modal']} onClick={handleDeleteNotif} style={'blue'} type="button">Hapus</Button>
          </div>
        </Box>
      </Modal>
      <ScrollToTop />
    </div>
  );
};

export default Notification;
